/* eslint-disable no-unused-vars */
import { useState, useEffect }                  from 'react';
import styled                                   from 'styled-components';
import { Segment, Grid, Divider, Icon, Header } from 'semantic-ui-react';
import { getAPI }                               from '../../../libs/fetchAPI';
import FlexBox                                  from '../../../components/FlexBox';
import Loading                                  from '../../../components/Loading';
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
	ArcElement,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
	ArcElement
);

const CustomFlexBox = styled(FlexBox)`
	justify-content: space-evenly;
`;

// Doughnut chart of runs this week with pass/fail
// Doughnut chart of specs ran this week with pass/fail
// Bar chart of last x days of runs with pass/fail
// Bar chart of last x days of specs ran with pass/fail
// Bar chart with each suite and how many times its been ran for the last 7 days, sorted with the hightest ran suites first. Stacked with pass/fail
// Bar chart of inactive specs for 7 day increments

function Dashboard() {
	const [ loading, setLoading ]              = useState(true);
	const [ dashboard_data, setDashboardData ] = useState({});

	useEffect(() => {
		fetchData();
	}, []);

	async function fetchData() {
		setLoading(true);
		const data = await getAPI(`/dashboard`);

		setDashboardData(data);
		setLoading(false);
	}

	if(loading) {
		return <Loading />;
	}

	return (
		<div>
			<Segment>
				<Grid columns={3} stackable textAlign='center'>
					<Grid.Row verticalAlign='middle'>
						<Grid.Column>
							<Header as='h3' icon color='blue'>
								<Icon name='play circle outline' />
								{dashboard_data?.active_specs ?? 0} Active Specs
							</Header>

						</Grid.Column>

						<Grid.Column>
							<Header as='h3' icon color='green'>
								<Icon name='chart line' />
								{dashboard_data?.added_specs?.length ?? 0} Added specs this week
							</Header>
						</Grid.Column>

						<Grid.Column>
							<Header as='h3' icon color='yellow'>
								<Icon name='pause circle outline' />
								{dashboard_data?.inactive_specs?.length ?? 0} Inactive Tests
							</Header>
						</Grid.Column>
					</Grid.Row>
				</Grid>
			</Segment>

			<CustomFlexBox>
				<div style={{ height: "400px", width: "50%" }}>
					<RunsPerDay run_data={dashboard_data.runs_pass_fail ?? []} />
				</div>

				<div style={{ height: "400px", width: "50%" }}>
					<SpecsPerDay spec_data={dashboard_data.specs_pass_fail ?? []} />
				</div>

				{/* <div style={{ height: "400px", width: "33%" }}>
					<InactiveSpecs />
				</div> */}
			</CustomFlexBox>

			<Divider />

			<CustomFlexBox>
				<div style={{ height: "400px", width: "100%" }}>
					<SuiteActivity activity={dashboard_data.suite_activity} />
				</div>

				{/* <div style={{ height: "400px", width: "33%" }}>
					<ActiveSpecs />
				</div> */}

				{/* <div style={{ height: "400px", width: "33%" }}>
					<ActiveSpecs />
				</div> */}
			</CustomFlexBox>
		</div>
	);
}

function RunsPerDay({ run_data }) {
	const options = {
		plugins: {
			tooltip : {
				mode      : `nearest`,
				intersect : true,
			},
			title: {
				display : true,
				text    : 'Test runs each day - Pass/Fail',
			},
			legend : {
				display : false,
			}
		},
		responsive : true,
		scales: {
			x: {
				stacked: true,
			},
			y: {
				stacked: true,
			},
		},
		barThickness: 5,
		base : 0,
		// minBarLength: 20,
		maintainAspectRatio: false,
	};

	const labels = run_data.map(run => run.formatted_run_date);

	const data = {
		labels,
		datasets: [
			{
				label: 'Passed',
				data: run_data.map(run => run.passed_runs),
				backgroundColor: 'green',
				barThickness: 7,
				categoryPercentage: 0.4,
				barPercentage: 0.4,
			},
			{
				label: 'Failed',
				data: run_data.map(run => run.failed_runs),
				backgroundColor: 'red',
				barThickness: 7,
				categoryPercentage: 0.4,
				barPercentage: 0.4,
			},
		],
	};

	return (
		<>
			<Bar data={data} options={options} />
		</>
	)
}

function SpecsPerDay({ spec_data }) {
	const options = {
		plugins: {
			tooltip : {
				mode      : `nearest`,
				intersect : true,
			},
			title: {
				display : true,
				text    : 'Spec files ran each day - Pass/Fail',
			},
			legend : {
				display : false,
			}
		},
		responsive : true,
		scales: {
			x: {
				stacked: true,
			},
			y: {
				stacked: true,
			},
		},
		barThickness: 5,
		base : 0,
		// minBarLength: 20,
		maintainAspectRatio: false,
	};

	const labels = spec_data.map(run => run.formatted_run_date);

	const data = {
		labels,
		datasets: [
			{
				label: 'Passed',
				data: spec_data.map(run => run.passed_specs),
				backgroundColor: 'green',
				barThickness: 7,
				categoryPercentage: 0.4,
				barPercentage: 0.4,
			},
			{
				label: 'Failed',
				data: spec_data.map(run => run.failed_specs),
				backgroundColor: 'red',
				barThickness: 7,
				categoryPercentage: 0.4,
				barPercentage: 0.4,
			},
		],
	};

	return (
		<>
			<Bar data={data} options={options} />
		</>
	)
}

function SuiteActivity({ activity }) {
	const options = {
		plugins: {
			tooltip : {
				mode      : `nearest`,
				intersect : true,
			},
			title: {
				display : true,
				text    : 'Suite Activity Last 30 Days',
			},
			legend : {
				display : false,
			}
		},
		responsive : true,
		scales: {
			x: {
				stacked : true,
				ticks   : {
					autoSkip: false,
				}
			},
			y: {
				stacked: true,
			},
		},
		barThickness: 6,
		base : 0,
		// minBarLength: 20,
		maintainAspectRatio: false,
	};

	const labels = activity.map(act => act.suite);

	const data = {
		labels,
		datasets: [
			{
				data: activity.map(act => act.run_count),
				backgroundColor: 'green',
				barThickness: 20,
				categoryPercentage: 0.4,
				barPercentage: 0.4,
			},
		],
	};

	return (
		<>
			<Bar data={data} options={options} />
		</>
	);
}

function InactiveSpecs() {
	const options = {
		plugins: {
			title: {
				display : true,
				text    : 'Inactive Specs',
			},
		},
		responsive : true,
		scales: {
			x: {
				stacked: true,
			},
			y: {
				stacked: true,
			},
		},
		barThickness: 10,
	};

	const labels = ['January', 'February', 'March', 'April', 'May', 'June', 'July'];

	const data = {
		labels,
		datasets: [
			{
				label: 'Dataset 1',
				data: labels.map(() => 10),
				backgroundColor: 'rgb(255, 99, 132)',
			},
			{
				label: 'Dataset 2',
				data: labels.map(() => 2),
				backgroundColor: 'rgb(75, 192, 192)',
			},
		],
	};

	return (
		<>
			<Bar data={data} options={options} />
		</>
	)
}

export default Dashboard;
